/**
 * Tenant Team & Seats panel (multi-tenancy, v0.13.0)
 *
 * The client self-administration half of the model: a workspace Owner or Admin invites their
 * own people, changes their roles, deactivates them, and removes them — without Akhile being
 * involved. Akhile only creates the workspace, sets its purchased seats, and invites the
 * first Owner.
 *
 * Deactivate and Remove both revoke access and both free the seat; the difference is what
 * survives. Deactivate keeps the membership, so the person stays listed and can be let back
 * in. It is what the API directs an admin to when deleting a user is refused for having
 * history — see `handleStatusChange`.
 *
 * Mounted at the top of /admin/users rather than as a new page, so "who is in my workspace"
 * and "what can they do" stay in one place.
 *
 * The `isTenantAdmin` checks below are currently belt-and-braces: /admin/users is already
 * behind `requireTenantAdmin`, so the read-only branches never render today. They are kept
 * so the panel can be dropped onto a non-admin surface (a plain "my team" page) without
 * silently exposing the invite, deactivate and remove controls.
 *
 * Two things worth understanding from the numbers shown here:
 *   - An invitation that has not been accepted yet still holds its seat. Otherwise a
 *     workspace could invite past its limit and only discover it when people tried to join.
 *   - Akhile staff appear with a "no seat" marker: they are members with seat_counted=false,
 *     so they never count against what the client has paid for.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Trash2, UserCheck, UserMinus, UserPlus, Users } from 'lucide-react';

import Button from '../Button';
import Card from '../Card';
import Input from '../Input';
import Select from '../Select';
import Badge from '../Badge';
import { useAuth } from '../../contexts/AuthContext';
import * as tenantAPI from '../../api/tenants';
import { TENANT_ROLES } from '../../utils/permissions';
import type { MembershipStatus, TenantMember, TenantRole, TenantWithSeats } from '../../types/auth';
import showToast from '../../utils/toast';

const ROLE_OPTIONS = TENANT_ROLES.map((r) => ({ value: r, label: r }));

const TenantTeamPanel: React.FC = () => {
  const { isTenantAdmin, user } = useAuth();

  const [tenant, setTenant] = useState<TenantWithSeats | null>(null);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<TenantRole>('Member');
  const [inviting, setInviting] = useState(false);

  /**
   * `silent` refreshes without collapsing the panel back to its spinner, so a role change or
   * removal leaves the surrounding page where the user left it.
   */
  const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [t, m] = await Promise.all([
        tenantAPI.getCurrentTenant(),
        tenantAPI.getCurrentTenantMembers(),
      ]);
      setTenant(t);
      setMembers(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your workspace team');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      await tenantAPI.inviteToCurrentTenant({
        email: inviteEmail.trim(),
        tenant_role: inviteRole,
      });
      showToast.success(`Invitation sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      setInviteOpen(false);
      await load({ silent: true });
    } catch (e) {
      // The seat cap is a hard 403 with an explanatory message; surface it verbatim.
      showToast.error(e instanceof Error ? e.message : 'Could not send the invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (member: TenantMember, role: TenantRole) => {
    if (role === member.tenant_role) return;
    setBusyId(member.id);
    try {
      await tenantAPI.updateCurrentTenantMember(member.id, { tenant_role: role });
      showToast.success(`${member.full_name || member.username} is now ${role}`);
      await load({ silent: true });
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : 'Could not change the role');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Deactivating revokes access while keeping the membership — which is what the API asks for
   * when a delete is refused. Deleting a user who has logged time or filed a weekly plan now
   * answers 409 ("deactivate their tenant membership instead"), because that history is the
   * record of work done and must stay attributed to the person who did it. Before this
   * control existed the panel offered no way to carry out that instruction: the only member
   * action was Remove, which deletes the membership outright.
   *
   * Unlike Remove this is reversible, so it applies on one click rather than arming a
   * confirmation — the same treatment as the role Select, which is comparably consequential.
   *
   * Reactivating re-takes a seat and the API re-checks the cap, so it can come back 403;
   * that message explains itself and is surfaced verbatim.
   */
  const handleStatusChange = async (member: TenantMember, status: MembershipStatus) => {
    setBusyId(member.id);
    try {
      await tenantAPI.updateCurrentTenantMember(member.id, { status });
      const name = member.full_name || member.username;
      showToast.success(
        status === 'deactivated'
          ? `${name} can no longer access this workspace. Their history stays attributed to them.`
          : `${name} has access to this workspace again`
      );
      await load({ silent: true });
    } catch (e) {
      showToast.error(
        e instanceof Error
          ? e.message
          : `Could not ${status === 'deactivated' ? 'deactivate' : 'reactivate'} this member`
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (member: TenantMember) => {
    setConfirmRemoveId(null);
    setBusyId(member.id);
    try {
      await tenantAPI.removeCurrentTenantMember(member.id);
      showToast.success(`${member.full_name || member.username} removed from this workspace`);
      await load({ silent: true });
    } catch (e) {
      showToast.error(e instanceof Error ? e.message : 'Could not remove this member');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <Card.Body>
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" aria-label="Loading team" />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (error || !tenant) {
    return (
      <Card className="mb-6">
        <Card.Body>
          <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" aria-hidden="true" />
            {error ?? 'Workspace unavailable'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  const noSeatsLeft = tenant.seats_available <= 0;

  return (
    <Card className="mb-6">
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5" aria-hidden="true" />
              {tenant.name} — Team &amp; Seats
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              <strong>{tenant.seats_used}</strong> of <strong>{tenant.seats_purchased}</strong>{' '}
              seats in use
              {noSeatsLeft && (
                <span className="text-amber-600 dark:text-amber-400">
                  {' '}— no seats free. Contact Akhile to add more before inviting anyone else.
                </span>
              )}
            </p>
          </div>
          {isTenantAdmin && (
            <Button variant="outline" leftIcon={UserPlus} onClick={() => setInviteOpen((v) => !v)}>
              Invite someone
            </Button>
          )}
        </div>
      </Card.Header>

      <Card.Body>
        {inviteOpen && isTenantAdmin && (
          <form
            onSubmit={handleInvite}
            className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4 items-end"
          >
            <Input
              label="Email address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@example.com"
              helperText="They will get a link to set a password"
              required
            />
            <Select
              label="Role in this workspace"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as TenantRole)}
              options={ROLE_OPTIONS}
            />
            <Button
              type="submit"
              variant="primary"
              isLoading={inviting}
              disabled={noSeatsLeft}
              leftIcon={UserPlus}
            >
              Send invitation
            </Button>
          </form>
        )}

        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {members.map((member) => {
            const isSelf = member.user_id === user?.id;
            const busy = busyId === member.id;
            return (
              <li key={member.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                      {member.full_name || member.username}
                    </span>
                    {member.status === 'invited' && <Badge variant="warning">Invited</Badge>}
                    {member.status === 'deactivated' && <Badge variant="default">Deactivated</Badge>}
                    {!member.seat_counted && <Badge variant="info">No seat</Badge>}
                    {isSelf && <Badge variant="default">You</Badge>}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{member.email}</p>
                </div>

                <div className="flex items-center gap-2">
                  {isTenantAdmin ? (
                    <Select
                      aria-label={`Role for ${member.username}`}
                      value={member.tenant_role}
                      disabled={busy}
                      onChange={(e) => handleRoleChange(member, e.target.value as TenantRole)}
                      options={ROLE_OPTIONS}
                      className="w-48"
                    />
                  ) : (
                    <Badge variant="info">{member.tenant_role}</Badge>
                  )}

                  {/* Deactivate is offered before Remove: it is the reversible one, and the
                      one the API names when it refuses to delete someone who has history.

                      Hidden for yourself. The API guards self-removal with a 400 but has no
                      equivalent guard on a status change, so a workspace admin could revoke
                      their own access and have no way back in.

                      Offered for active members only, never for an invitation still
                      outstanding. Reactivating sets the status straight to 'active', which
                      for someone who never accepted would skip the set-password step and
                      leave them active with no way to sign in. Use Remove to rescind an
                      invitation — there is no history to preserve yet. */}
                  {isTenantAdmin && !isSelf && member.status === 'active' && (
                    <Button
                      variant="outline"
                      disabled={busy}
                      leftIcon={UserMinus}
                      onClick={() => handleStatusChange(member, 'deactivated')}
                      title="Revoke access but keep their history attributed to them"
                    >
                      Deactivate
                    </Button>
                  )}

                  {isTenantAdmin && !isSelf && member.status === 'deactivated' && (
                    <Button
                      variant="outline"
                      disabled={busy}
                      leftIcon={UserCheck}
                      onClick={() => handleStatusChange(member, 'active')}
                      title="Restore access — this re-takes a seat"
                    >
                      Reactivate
                    </Button>
                  )}

                  {/* Removing yourself is refused by the API — hide it rather than offer a
                      button that always fails.

                      A bare trash icon one click away from removing a colleague and moving the
                      workspace's seat usage is too easy to hit by accident and cannot be undone,
                      so the icon arms the confirmation rather than doing the work. */}
                  {isTenantAdmin && !isSelf &&
                    (confirmRemoveId === member.id ? (
                      <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        Remove?
                        <Button variant="danger" disabled={busy} onClick={() => handleRemove(member)}>
                          Remove
                        </Button>
                        <Button variant="outline" onClick={() => setConfirmRemoveId(null)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() => setConfirmRemoveId(member.id)}
                        aria-label={`Remove ${member.username} from this workspace`}
                      >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    ))}
                </div>
              </li>
            );
          })}
        </ul>

        {members.length === 0 && (
          <p className="text-sm text-gray-600 dark:text-gray-400 py-4">
            No one else is in this workspace yet.
          </p>
        )}
      </Card.Body>
    </Card>
  );
};

export default TenantTeamPanel;
